import { gql } from './client';
import { emit } from './events';

export interface PlanningAssessment {
  id: string;
  ownerUserId?: string | null;
  subjectName?: string;
  status: string;
  score?: number;
  riskBand?: string;
  analysis?: string;
  documentName?: string;
  /** Pre-application checks: the description this check was run against. */
  proposalDescription?: string | null;
  /** The description refines the earlier ones (continues the conversation) rather than replacing them. */
  proposalRefines?: boolean | null;
  inputTokens?: number;
  outputTokens?: number;
  estimatedCostUsd?: number;
  truncated?: boolean;
  finalTextTruncated?: boolean;
  chunked?: boolean;
  chunkCount?: number;
  error?: string;
  assessedAt?: string;
  createdAt: string;
  version?: string;
  pdf?: { id: string; filename: string } | null;
}

const FIELDS = `id subjectName status score riskBand analysis documentName proposalDescription proposalRefines inputTokens outputTokens
  estimatedCostUsd truncated finalTextTruncated chunked chunkCount error assessedAt createdAt version
  pdf { id filename }`;

export async function getPlanningAssessment(id: string): Promise<PlanningAssessment | undefined> {
  const data = await gql<{ elevationsPlanningAssessment: PlanningAssessment | null }>(
    `query GetPlanningAssessment($id: ID!) { elevationsPlanningAssessment(id: $id) { ${FIELDS} } }`,
    { id },
  );
  return data.elevationsPlanningAssessment ?? undefined;
}

// entityId is the case id: the assessment-run workflow reads it as the event subject.
export function emitRequestPlanningAssessment(caseId: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('RequestPlanningAssessment', caseId, { performedBy }, connectionId);
}

// Re-runs only the PDF step for an assessment that already finished; the analysis is not repeated or charged.
export function emitRetryPlanningAssessmentPdf(assessmentId: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('RetryPlanningAssessmentPdf', assessmentId, { performedBy }, connectionId);
}

export function emitDeletePlanningAssessment(id: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('DeletePlanningAssessment', id, { performedBy }, connectionId);
}

// Dev mode (an administrator's AI setting): a run stops before the model as 'AwaitingReply', holding exactly what the
// model would have been sent, and is completed by pasting Claude's reply back.
export async function getAssessmentRunState(id: string): Promise<{ status: string; model: string | null } | undefined> {
  const data = await gql<{ elevationsPlanningAssessment: { status: string; model: string | null } | null }>(
    'query AssessmentRunState($id: ID!) { elevationsPlanningAssessment(id: $id) { status model } }',
    { id },
  );
  return data.elevationsPlanningAssessment ?? undefined;
}

export async function getDevPrompt(id: string): Promise<string | null> {
  const data = await gql<{ elevationsPlanningAssessment: { devPrompt: string | null } | null }>(
    'query AssessmentDevPrompt($id: ID!) { elevationsPlanningAssessment(id: $id) { devPrompt } }',
    { id },
  );
  return data.elevationsPlanningAssessment?.devPrompt ?? null;
}

// Gzipped then base64-encoded: an event payload is capped at 64 KB and a full analysis can come close.
async function gzipBase64(text: string): Promise<string> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
  const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

// The case is the event's subject: the workflow reads its assessments from the graph to check this run is one of them.
export async function emitDevReply(caseId: string, assessmentId: string, reply: string | null, performedBy?: string, connectionId?: string): Promise<string> {
  const payload = reply == null ? { assessmentId, abandon: true, performedBy } : { assessmentId, replyGz: await gzipBase64(reply), performedBy };
  return emit('SubmitPlanningAssessmentDevReply', caseId, payload, connectionId);
}
